import { CircularProgress } from "@mui/material";
import DashboardLayout from "components/layout/DashboardLayout";
import Loader from "components/Loader";
import { Roles, ROUTES, ROUTES_PERMISSIONS } from "constants/index";
import AdditionalItemsPage from "pages/AdditionalItemsPage";
import AdditionalsPage from "pages/AdditionalsPage";
import ArticlesPage from "pages/ArticlesPage";
import AssociatedClientsPage from "pages/AssociatedClientsPage";
import ChangePasswordPage from "pages/ChangePassword";
import ClientsPage from "pages/ClientsPage";
import ConfigsPage from "pages/ConfigsPage";
import ContainersPage from "pages/ContainersPage";
import DischargesPage from "pages/DischargesPage";
import GroupsPage from "pages/GroupsPage";
import HistoryPage from "pages/HistoryPage";
import HomePage from "pages/HomePage";
import FractionationPage from "pages/FractionationPage";
import FractionationDetailPage from "pages/FractionationDetailPage";
import FractionationOrdersProvider from "providers/FractionationOrdersProvider";
import IncomePage from "pages/IncomePage";
import IncomeEntryDetailsPage from "pages/IncomeEntryDetailsPage";
import IncomeEntryPage from "pages/IncomeEntryPage";
import IncomeEntryDetailReviewPage from "pages/IncomeEntryDetailReviewPage";
import { LiquidationDetailsPage } from "pages/LiquidationDetailsPage";
import LiquidationsPage from "pages/LiquidationsPage";
import LocationsPage from "pages/LocationsPage";
import LoginPage from "pages/LoginPage";
import OperationsPage from "pages/OperationsPage";
import OrdersPage from "pages/OrdersPage";
import OperatorOrderDetailPage from "pages/OperatorOrderDetailPage";
import OperatorOrdersPage from "pages/OperatorOrdersPage";
import PrePickingPage from "pages/PrePickingPage";
import RouteErrorPage from "pages/RouteErrorPage";
import StoresPage from "pages/StoresPage";
import SublocationsPage from "pages/SublocationsPage";
import TransferPage from "pages/TransferPage";
import UnitsPage from "pages/UnitsPage";
import UserSettingsPage from "pages/UserSettingsPage";
import UsersPage from "pages/UsersPage";
import { useSession } from "providers/SessionProvider";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouteObject,
  RouterProvider,
} from "react-router-dom";

export const Router = () => {
  const { userData, isLoggedIn, isLoadingUser } = useSession();
  const userRole = userData?.role || "";

  if (isLoadingUser) {
    return <Loader />;
  }

  const routes: RouteObject[] = [
    {
      path: ROUTES.INDEX,
      element: isLoggedIn() ? (
        <Navigate to={ROUTES.HOME} replace />
      ) : (
        <Navigate to={ROUTES.LOGIN} replace />
      ),
      errorElement: <RouteErrorPage />,
    },
    {
      path: ROUTES.LOGIN,
      element: isLoggedIn() ? (
        <Navigate to={ROUTES.HOME} replace />
      ) : (
        <LoginPage />
      ),
      errorElement: <RouteErrorPage />,
    },
    {
      element: (
        <DashboardLayout>
          <Outlet />
        </DashboardLayout>
      ),
      errorElement: <RouteErrorPage />,
      children: [
        {
          path: ROUTES.HOME,
          element: isLoggedIn() ? (
            <HomePage />
          ) : (
            <Navigate to={ROUTES.LOGIN} replace />
          ),
        },
        {
          path: ROUTES.ORDERS,
          element: ROUTES_PERMISSIONS[ROUTES.ORDERS].includes(userRole) ? (
            userRole === Roles.OPERATOR ? <OperatorOrdersPage /> : <OrdersPage />
          ) : <Navigate to={ROUTES.INDEX} replace />,
        },
        {
          path: ROUTES.ORDERS_DETAIL,
          element: ROUTES_PERMISSIONS[ROUTES.ORDERS_DETAIL].includes(userRole) ? (
            <OperatorOrderDetailPage />
          ) : <Navigate to={ROUTES.INDEX} replace />,
        },
        {
          path: ROUTES.INCOME,
          element: ROUTES_PERMISSIONS[ROUTES.INCOME].includes(userRole) ? (
            <IncomePage />
          ) : (
            <Navigate to={ROUTES.INDEX} replace />
          ),
        },
        {
          path: ROUTES.INCOME_WITH_DRAFT,
          element: ROUTES_PERMISSIONS[ROUTES.INCOME_WITH_DRAFT].includes(
            userRole,
          ) ? (
            <IncomePage />
          ) : (
            <Navigate to={ROUTES.INDEX} replace />
          ),
        },
        {
          element: ROUTES_PERMISSIONS[ROUTES.FRACTIONATION].includes(userRole) ? (
            <FractionationOrdersProvider>
              <Outlet />
            </FractionationOrdersProvider>
          ) : (
            <Navigate to={ROUTES.INDEX} replace />
          ),
          children: [
            {
              path: ROUTES.FRACTIONATION,
              element: <FractionationPage />,
            },
            {
              path: ROUTES.FRACTIONATION_DETAIL,
              element: <FractionationDetailPage />,
            },
          ],
        },
        {
          path: ROUTES.INCOME_ENTRY,
          element: ROUTES_PERMISSIONS[ROUTES.INCOME_ENTRY].includes(userRole) ? (
            <IncomeEntryPage />
          ) : (
            <Navigate to={ROUTES.INDEX} replace />
          ),
        },
        {
          path: ROUTES.INCOME_ENTRY_DETAIL,
          element: ROUTES_PERMISSIONS[ROUTES.INCOME_ENTRY_DETAIL].includes(userRole) ? (
            <IncomeEntryDetailsPage />
          ) : (
            <Navigate to={ROUTES.INDEX} replace />
          ),
        },
        {
          path: ROUTES.INCOME_ENTRY_DETAIL_REVIEW,
          element: ROUTES_PERMISSIONS[ROUTES.INCOME_ENTRY_DETAIL_REVIEW].includes(userRole) ? (
            <IncomeEntryDetailReviewPage />
          ) : (
            <Navigate to={ROUTES.INDEX} replace />
          ),
        },
        {
          path: ROUTES.DISCHARGE,
          element: ROUTES_PERMISSIONS[ROUTES.DISCHARGE].includes(userRole) ? (
            <DischargesPage />
          ) : (
            <Navigate to={ROUTES.INDEX} replace />
          ),
        },
        {
          path: ROUTES.DISCHARGE_WITH_DRAFT,
          element: ROUTES_PERMISSIONS[ROUTES.DISCHARGE_WITH_DRAFT].includes(
            userRole,
          ) ? (
            <DischargesPage />
          ) : (
            <Navigate to={ROUTES.INDEX} replace />
          ),
        },
        {
          path: ROUTES.PREPICKING,
          element: ROUTES_PERMISSIONS[ROUTES.PREPICKING].includes(userRole) ? (
            <PrePickingPage />
          ) : (
            <Navigate to={ROUTES.INDEX} replace />
          ),
        },
        {
          path: ROUTES.ADDITIONALS,
          element: ROUTES_PERMISSIONS[ROUTES.ADDITIONALS].includes(userRole) ? (
            <AdditionalsPage />
          ) : (
            <Navigate to={ROUTES.INDEX} replace />
          ),
        },
        {
          path: ROUTES.TRANSFER,
          element: ROUTES_PERMISSIONS[ROUTES.TRANSFER].includes(userRole) ? (
            <TransferPage />
          ) : (
            <Navigate to={ROUTES.INDEX} replace />
          ),
        },
        {
          path: ROUTES.OPERATIONS,
          element: ROUTES_PERMISSIONS[ROUTES.OPERATIONS].includes(userRole) ? (
            <OperationsPage />
          ) : (
            <Navigate to={ROUTES.INDEX} replace />
          ),
        },
        {
          path: ROUTES.HISTORY,
          element: ROUTES_PERMISSIONS[ROUTES.HISTORY].includes(userRole) ? (
            <HistoryPage />
          ) : (
            <Navigate to={ROUTES.INDEX} replace />
          ),
        },
        {
          path: ROUTES.SALES,
          element: ROUTES_PERMISSIONS[ROUTES.SALES].includes(userRole) ? (
            <LiquidationsPage />
          ) : (
            <Navigate to={ROUTES.INDEX} replace />
          ),
        },
        {
          path: ROUTES.SALES_DETAIL,
          element: ROUTES_PERMISSIONS[ROUTES.SALES_DETAIL].includes(
            userRole,
          ) ? (
            <LiquidationDetailsPage />
          ) : (
            <Navigate to={ROUTES.INDEX} replace />
          ),
        },
        {
          path: ROUTES.LISTS,
          errorElement: <RouteErrorPage />,
          children: [
            {
              path: ROUTES.CLIENTS,
              element: ROUTES_PERMISSIONS[ROUTES.CLIENTS].includes(userRole) ? (
                <ClientsPage />
              ) : (
                <Navigate to={ROUTES.INDEX} replace />
              ),
            },
            {
              path: ROUTES.USERS,
              element: ROUTES_PERMISSIONS[ROUTES.USERS].includes(userRole) ? (
                <UsersPage />
              ) : (
                <Navigate to={ROUTES.INDEX} replace />
              ),
            },
            {
              path: ROUTES.ASSOCIADED_CLIENTS,
              element: ROUTES_PERMISSIONS[ROUTES.ASSOCIADED_CLIENTS].includes(
                userRole,
              ) ? (
                <AssociatedClientsPage />
              ) : (
                <Navigate to={ROUTES.INDEX} replace />
              ),
            },
            {
              path: ROUTES.PRODUCTS,
              element: ROUTES_PERMISSIONS[ROUTES.PRODUCTS].includes(
                userRole,
              ) ? (
                <ArticlesPage />
              ) : (
                <Navigate to={ROUTES.INDEX} replace />
              ),
            },
            {
              path: ROUTES.GROUPS,
              element: ROUTES_PERMISSIONS[ROUTES.GROUPS].includes(userRole) ? (
                <GroupsPage />
              ) : (
                <Navigate to={ROUTES.INDEX} replace />
              ),
            },
            {
              path: ROUTES.STORES,
              element: ROUTES_PERMISSIONS[ROUTES.STORES].includes(userRole) ? (
                <StoresPage />
              ) : (
                <Navigate to={ROUTES.INDEX} replace />
              ),
            },
            {
              path: ROUTES.LOCATIONS,
              element: ROUTES_PERMISSIONS[ROUTES.LOCATIONS].includes(
                userRole,
              ) ? (
                <LocationsPage />
              ) : (
                <Navigate to={ROUTES.INDEX} replace />
              ),
            },
            {
              path: ROUTES.SUBLOCATIONS,
              element: ROUTES_PERMISSIONS[ROUTES.SUBLOCATIONS].includes(
                userRole,
              ) ? (
                <SublocationsPage />
              ) : (
                <Navigate to={ROUTES.INDEX} replace />
              ),
            },
            {
              path: ROUTES.UNITS,
              element: ROUTES_PERMISSIONS[ROUTES.UNITS].includes(userRole) ? (
                <UnitsPage />
              ) : (
                <Navigate to={ROUTES.INDEX} replace />
              ),
            },
            {
              path: ROUTES.CONTAINERS,
              element: ROUTES_PERMISSIONS[ROUTES.CONTAINERS].includes(
                userRole,
              ) ? (
                <ContainersPage />
              ) : (
                <Navigate to={ROUTES.INDEX} replace />
              ),
            },
            {
              path: ROUTES.ADDITIONAL_ITEMS,
              element: ROUTES_PERMISSIONS[ROUTES.ADDITIONAL_ITEMS].includes(
                userRole,
              ) ? (
                <AdditionalItemsPage />
              ) : (
                <Navigate to={ROUTES.INDEX} replace />
              ),
            },
          ],
        },
        {
          path: ROUTES.CHANGE_PASSWORD,
          element: ROUTES_PERMISSIONS[ROUTES.CHANGE_PASSWORD].includes(
            userRole,
          ) ? (
            <ChangePasswordPage />
          ) : (
            <Navigate to={ROUTES.INDEX} replace />
          ),
        },
        {
          path: ROUTES.USER_SETTINGS,
          element: ROUTES_PERMISSIONS[ROUTES.USER_SETTINGS].includes(
            userRole,
          ) ? (
            <UserSettingsPage />
          ) : (
            <Navigate to={ROUTES.INDEX} replace />
          ),
        },
        {
          path: ROUTES.CONFIGS,
          element: ROUTES_PERMISSIONS[ROUTES.CONFIGS].includes(userRole) ? (
            <ConfigsPage />
          ) : (
            <Navigate to={ROUTES.INDEX} replace />
          ),
        },
      ],
    },
  ];

  const router = createBrowserRouter(routes);

  return (
    <RouterProvider router={router} fallbackElement={<CircularProgress />} />
  );
};
